import i18n from '@/i18n';
import store from '../store';
import { findSupportedLocale } from '@/utils/i18n-helpers';

/**
 * Filter out unanswered questions from a custom questions array
 *
 * @param {CustomQuestion[]} questions
 * @return {CustomQuestion[]}
 */
export function filterAnsweredQuestions(questions: CustomQuestion[]): CustomQuestion[] {
	return questions.filter((question: CustomQuestion) => {
		if (question.question_type === 'checkbox') {
			return 'checked' in question;
		}
		if (question.question_type === 'timepicker') {
			return question.timeSelected !== '';
		}
		if (question.question_type === 'open') {
			return question.answer !== '';
		}
		return question.answerObj && Object.keys(question.answerObj).length;
	});
}


/**
 * Format the answered questions object depending of the question's type
 *
 * @param {CustomQuestion} question
 * @param {boolean} includeType
 * @return {CheckoutCustomQuestion}
 */
export function formatAnsweredQuestion(question: CustomQuestion, includeType: boolean = false): CheckoutCustomQuestion {
	const restaurant: Restaurant = store.getters['restaurant/getRestaurant'];
	let answer: string;

	if (question.question_type === 'checkbox') {
		const defaultLocale = restaurant.locales?.find((locale: RestaurantLocale) => locale.is_default);
		const supportedLocale = defaultLocale?.locale_short ? findSupportedLocale(defaultLocale.locale_short) : null;
		const translationKey = `checkout.form.custom_questions.${(question as QuestionWithCheckbox).checked ? 'checked' : 'unchecked'}`;
		answer = supportedLocale ? i18n.t(translationKey, supportedLocale.code) : i18n.t(translationKey);
	}
	else if (question.question_type === 'timepicker') {
		answer = (question as QuestionWithTimePicker).timeSelected;
		includeType = true; // always include type for timepicker so Gusteau knows the answer is a datetime
	}
	else if (question.question_type === 'open') {
		answer = (question as QuestionWithOpenAnswer).answer;
	}
	else {
		const index = (question as QuestionWithOptions).answerObj?.index as number;
		answer = (question as QuestionWithOptions).options[index];
	}

	const answeredQuestion: CheckoutCustomQuestion = { question: question.question, answer };

	if (includeType) {
		answeredQuestion.question_type = question.question_type;
	}

	if (question.localization && Object.keys(question.localization).length) {
		const localization: Localization = {};
		for (const locale in question.localization) {
			if (question.question_type === 'checkbox') {
				const supportedLocale = findSupportedLocale(locale);
				localization[locale] = {
					question: question.localization[locale].question,
					answer: i18n.t(`checkout.form.custom_questions.${(question as QuestionWithCheckbox).checked ? 'checked' : 'unchecked'}`, supportedLocale!.code)
				};
			}
			else if (question.question_type === 'timepicker') {
				localization[locale] = {
					question: question.localization[locale].question
				};
			}
			else if (question.question_type === 'open') {
				localization[locale] = {
					question: question.localization[locale].question,
					answer: (question as QuestionWithOpenAnswer).answer
				};
			}
			else {
				const index = (question as QuestionWithOptions).answerObj?.index as number;
				localization[locale] = {
					question: question.localization[locale].question,
					answer: question.localization[locale].options![index]
				};
			}
		}
		answeredQuestion.localization = localization;
	}

	return answeredQuestion;
}

/**
 * Build the validation rules for a custom question.
 *
 * @param {CustomQuestion} question
 * @return {Record<string, any>}
 */
export function getQuestionRules(question: CustomQuestion): Record<string, any> | string {
	const rules: Record<string, any> = {};

	if (question.required) {
		rules.required = { allowFalse: false };
	}

	if (question.question_type === 'open') {
		rules.max = 480;
	}

	return Object.keys(rules).length ? rules : '';
}
